import { QueryClient, useQuery } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import localeData from "dayjs/plugin/localeData";
import { useEffect } from "react";

import { platformApi } from "@/app/platform";
import { DefaultErrorComponent } from "@/components/Errors/DefaultErrorComponent";
import { UnderMaintenance } from "@/components/UnderMaintenance/UnderMaintenance";
import { PaymentContextProvider } from "@/contexts/PaymentContext";
import { SnackbarProvider } from "@/contexts/SnackbarContext";
import { FlightSearchDataProvider } from "@/features/flights/contexts/FlightSearchDataContext";
import { FlightSortProvider } from "@/features/flights/contexts/FlightSortContext";
import { HotelFiltersProvider } from "@/features/hotels/contexts/HotelFiltersContext";
import { HotelSearchDataProvider } from "@/features/hotels/contexts/HotelSearchDataContext";
import { HotelSortProvider } from "@/features/hotels/contexts/HotelSortContext";
import { applicationStateQueryOptions } from "@/hooks/useApplicationStateTurnedOff";

dayjs.extend(durationPlugin);
dayjs.extend(localeData);

type HotelBookingStep = {
  stepNumber: number;
  presentationMode?: "modal";
};

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    hotelStep?: HotelBookingStep;
    drawer?: boolean;
  }
}

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RouteComponent,
  errorComponent: DefaultErrorComponent,
});

function RouteComponent() {
  const router = useRouterState();

  const { data: underMaintenance } = useQuery(applicationStateQueryOptions());

  useEffect(() => {
    platformApi.setNavigationBar(
      router.location.pathname === "/"
        ? {
            backgroundColor: "#003E2B",
            image: `${location.origin}/tzlogo.png`,
          }
        : {
            backgroundColor: "#f5f6f5",
            image: null,
          },
    );
  }, [router.location.pathname]);

  return (
    <SnackbarProvider>
      {underMaintenance ? (
        <main className="h-dvh w-dvh">
          <UnderMaintenance />
        </main>
      ) : (
        // TODO: Move this to a specific route
        <FlightSearchDataProvider>
          <FlightSortProvider>
            <HotelSearchDataProvider>
              <HotelFiltersProvider>
                <HotelSortProvider>
                  <PaymentContextProvider>
                    <Outlet />
                  </PaymentContextProvider>
                </HotelSortProvider>
              </HotelFiltersProvider>
            </HotelSearchDataProvider>
          </FlightSortProvider>
        </FlightSearchDataProvider>
      )}
    </SnackbarProvider>
  );
}
